import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Shield, Menu, LogOut, Settings, ChevronDown, Building2, User, ShieldCheck } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { dashboardApi } from "@/lib/api";
import { adminApi } from "@/lib/admin-api";
import { CURRENCIES, getPopularCurrencies } from "@/lib/currencies";
import HealthDot from "./HealthDot";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vault-truth/artifacts/s3q6fukn_image.png";

export default function TopBar() {
  const { user, trial, logout, currency, setCurrency } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const isAdmin = user?.role === "admin" || user?.is_admin === true;

  useEffect(() => {
    dashboardApi.summary().then(r => setSummary(r.data)).catch(() => {});
  }, []);

  // Poll the pending-approvals count every 30s for admins. Falls back
  // silently to 0 if the call errors so non-admins (or transient
  // failures) never show a stale badge.
  useEffect(() => {
    if (!isAdmin) return;
    let stopped = false;
    const tick = () => {
      adminApi.listPending()
        .then((r) => { if (!stopped) setPendingCount(r.data.count || 0); })
        .catch(() => { if (!stopped) setPendingCount(0); });
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { stopped = true; clearInterval(id); };
  }, [isAdmin]);

  const state = summary?.behaviour_state?.collapsed_state || "STRATEGIC";
  const probity = summary?.probity_score || 0;
  const daysToNext = summary?.days_to_next_obligation;

  const stateColors = {
    STRATEGIC: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    EMOTIONAL: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    SURVIVAL: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  return (
    <header className="h-14 bg-[#0B1120]/80 backdrop-blur-md border-b border-slate-800/50 flex items-center justify-between px-4 md:px-6 shrink-0" data-testid="topbar">
      <div className="flex items-center gap-4 lg:hidden">
        <button className="text-slate-400 hover:text-slate-200" data-testid="mobile-menu-btn">
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="topbar-home-link">
          <img src={LOGO_URL} alt="Sentient Vault" className="w-7 h-7 object-contain" />
          <span className="font-barlow font-bold text-sm">SENTIENT VAULT</span>
        </Link>
      </div>

      <div className="hidden lg:flex items-center gap-3">
        <Badge className={`${stateColors[state]} border text-xs font-mono px-3 py-1`} data-testid="behaviour-state-badge">
          Current: {state}
        </Badge>
        <Badge className="bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 text-xs font-mono px-3 py-1" data-testid="probity-score-badge">
          Probity: {probity}
        </Badge>
        {daysToNext !== null && daysToNext !== undefined && (
          <Badge className={`${daysToNext <= 7 ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-slate-800 text-slate-400 border-slate-700'} border text-xs font-mono px-3 py-1`} data-testid="next-obligation-badge">
            Next Due: {daysToNext}d
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Admin Console link — only visible to admins. Shows a pending
            count badge when users are awaiting approval. */}
        {isAdmin && (
          <button
            onClick={() => navigate("/admin")}
            className="relative h-8 px-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-md hover:bg-cyan-500/20 transition-colors"
            data-testid="admin-nav-link"
            title="Admin Console"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Admin</span>
            {pendingCount > 0 && (
              <span
                className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-amber-500 text-slate-950"
                data-testid="admin-pending-badge"
              >
                {pendingCount > 99 ? "99+" : pendingCount}
              </span>
            )}
          </button>
        )}

        {/* Silent deployment-parity dot. Non-blocking; admins can
            click it to see env/build_id/status. End users see only a
            tiny status indicator and an aria-label. */}
        <HealthDot isAdmin={isAdmin} />

        {/* Language is locked to English — no selector, just a static label */}
        <span
          className="h-8 px-2 inline-flex items-center text-[11px] font-mono text-slate-400 bg-slate-800/50 border border-slate-700 rounded-md select-none"
          data-testid="locale-label"
          aria-label="Locale: English"
        >
          🇬🇧 EN
        </span>

        {/* Currency Selector */}
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger className="w-[90px] h-8 text-xs bg-slate-800/50 border-slate-700" data-testid="currency-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px]">
            <div className="px-2 py-1 text-[9px] text-slate-600 uppercase tracking-wider">Popular</div>
            {getPopularCurrencies().map(c => (
              <SelectItem key={c.code} value={c.code} className="text-xs">
                <span className="font-mono">{c.code}</span> <span className="text-slate-500">{c.symbol}</span>
              </SelectItem>
            ))}
            <div className="px-2 py-1 mt-1 border-t border-slate-800 text-[9px] text-slate-600 uppercase tracking-wider">All Currencies</div>
            {CURRENCIES.filter(c => !getPopularCurrencies().find(p => p.code === c.code)).map(c => (
              <SelectItem key={c.code} value={c.code} className="text-xs">
                <span className="font-mono">{c.code}</span> <span className="text-slate-500">{c.symbol}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Company Identity Chip + Dropdown */}
        <CompanyMenu user={user} trial={trial} logout={logout} navigate={navigate} />
      </div>
    </header>
  );
}

function CompanyMenu({ user, trial, logout, navigate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const companyName = trial?.company_name || user?.company_name || "My Company";
  const companyInitial = companyName.charAt(0).toUpperCase();
  const userName = user?.full_name || "User";
  const userEmail = user?.email || "";
  const plan = trial?.plan || trial?.trial_status || "TRIAL";

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate("/");
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-800/50 transition-colors" data-testid="company-menu-btn">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/80 to-amber-700 flex items-center justify-center text-xs font-bold text-slate-900 shrink-0">
          {companyInitial}
        </div>
        <div className="hidden md:block text-left">
          <div className="text-xs text-slate-200 font-semibold leading-tight truncate max-w-[140px]">{companyName}</div>
          <div className="text-[10px] text-slate-500 leading-tight">{userName}</div>
        </div>
        <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 rounded-xl border border-slate-800/60 bg-[#0B1120] shadow-2xl z-50 overflow-hidden" data-testid="company-dropdown">
          {/* Company Info */}
          <div className="p-3 border-b border-slate-800/40">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/80 to-amber-700 flex items-center justify-center text-sm font-bold text-slate-900">
                {companyInitial}
              </div>
              <div className="min-w-0">
                <div className="text-sm text-slate-100 font-semibold truncate">{companyName}</div>
                <div className="text-[10px] text-slate-500 truncate">{userEmail}</div>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Badge className={`text-[8px] font-mono ${plan === "ACTIVE" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-cyan-500/15 text-cyan-400 border-cyan-500/30"} border`}>
                {plan}
              </Badge>
              <span className="text-[9px] text-slate-600">{user?.role}</span>
            </div>
          </div>

          {/* Menu Items */}
          <div className="p-1.5">
            <Link to="/dashboard/profile" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-colors" data-testid="company-profile-link">
              <Building2 className="w-3.5 h-3.5" /> Company Profile
            </Link>
            <Link to="/checkout" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-colors">
              <Settings className="w-3.5 h-3.5" /> Manage Plan
            </Link>
          </div>

          {/* Logout */}
          <div className="p-1.5 border-t border-slate-800/40">
            <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors" data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Log Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
